import React from 'react';

import EmailInput from 'components/FormInputs/EmailInput/EmailInput';
import Logo from 'components/Logo/Logo';
import SubmitButton from 'components/SubmitButton/SubmitButton';
import { Formik, Form } from 'formik';
import Head from 'next/head';
import { useTranslation } from 'react-i18next';
import APIKit from 'services/APIKit';
import * as yup from 'yup';

import styles from './Login.module.scss';

interface ResetPasswordFormProps {
  email: string;
}

const ResetPassword = (): JSX.Element => {
  const { t } = useTranslation();

  const formValidationSchema = yup.object().shape({
    email: yup
      .string()
      .email(t('general.mustBeValidEmail'))
      .required(t('general.required')),
  });

  const [submitted, setSubmitted] = React.useState(false);

  const handleFormSubmit = async (
    values: ResetPasswordFormProps,
  ): Promise<void> => {
    const success = await APIKit.auth.resetPassword(values.email);
    if (success) {
      setSubmitted(true);
    }
  };

  return (
    <>
      <Head>
        <title>{t('pageTitle.resetPassword')}</title>
      </Head>
      <div className={styles.passwordContainer} data-test="reset-password-page">
        <Logo width={175} className={styles.logo} />
        <p className="px-4 mb-0 font-weight-bold text-gray-700">
          {t('general.forgotPassword.title')}
        </p>
        <p className="px-4 mb-3 text-gray-500">
          {t('general.forgotPassword.text')}
        </p>

        {submitted ? (
          <p className="px-4 py-3" data-test="reset-password-success">
            {t('resetPassword.success')}
          </p>
        ) : (
          <Formik
            initialValues={{ email: '' }}
            onSubmit={handleFormSubmit}
            validationSchema={formValidationSchema}
          >
            {({ isSubmitting, errors, touched }): JSX.Element => (
              <Form>
                <EmailInput
                  error={errors.email}
                  touched={touched.email}
                  required
                />

                <SubmitButton
                  isSubmitting={isSubmitting}
                  text={t('resetPassword.resetPassword')}
                />
              </Form>
            )}
          </Formik>
        )}
      </div>
      <img
        className={`d-none d-md-block ${styles.backgroundImage}`}
        src="/img/login.png"
        alt=""
      />
    </>
  );
};

export default ResetPassword;
